import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  People as PeopleIcon,
  Key as KeyIcon,
  Timeline as TimelineIcon,
  CloudDownload as DownloadIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { 
  api, 
  createAccessCode, 
  getAllAccessCodes, 
  updateAccessCode, 
  deleteAccessCode, 
  getActiveUsers 
} from '../services/api';
import Navbar from '../components/Navbar';
import { useAdminAuth } from '../hooks/useAdminAuth';

interface DashboardData {
  total_api_keys: number;
  allocated_api_keys: number;
  available_api_keys: number;
  api_keys_by_type: {
    text: { total: number; available: number; };
    image: { total: number; available: number; };
    video: { total: number; available: number; };
  };
  sessions: Session[];
  stats: SessionStats;
  recent_connections: UserConnection[];
}

interface Session {
  id: string;
  session_id: string;
  session_name?: string;
  description?: string;
  status: 'active' | 'expired' | 'terminated';
  max_connections: number;
  current_connections: number;
  total_connections: number;
  duration_hours: number;
  created_at: string;
  expires_at: string;
  terminated_at?: string;
}

interface SessionStats {
  total_sessions: number;
  active_sessions: number;
  expired_sessions: number;
  terminated_sessions: number;
  total_connections: number;
  active_connections: number;
}

interface UserConnection {
  id: string;
  session_id: string;
  user_number: number;
  api_key_index: number;
  status: 'connected' | 'disconnected' | 'inactive';
  connected_at: string;
  disconnected_at?: string;
  last_activity: string;
  user_agent?: string;
  ip_address?: string;
}

interface SessionCreateData {
  session_name?: string;
  description?: string;
  max_connections: number;
  duration_hours: number;
}

interface AccessCode {
  id: string;
  code: string;
  name?: string;
  is_active: boolean;
  max_users: number;
  current_users: number;
  max_video_generations: number;
  created_at: string;
  updated_at: string;
}

interface AccessCodeCreateData {
  code: string;
  name?: string;
  max_users: number;
  max_video_generations: number;
}

interface AccessCodeUser {
  id: string;
  access_code: string;
  user_number: number;
  api_key_index: number;
  user_agent?: string;
  ip_address?: string;
  connected_at: string;
  last_activity: string;
  is_active: boolean;
  video_generation_count: number;
}

const AdminDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAdminLoggedIn } = useAdminAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 액세스 코드 관련 state
  const [accessCodes, setAccessCodes] = useState<AccessCode[]>([]);
  const [createAccessCodeOpen, setCreateAccessCodeOpen] = useState(false);
  const [editAccessCodeOpen, setEditAccessCodeOpen] = useState(false);
  const [accessCodeToEdit, setAccessCodeToEdit] = useState<AccessCode | null>(null);
  const [accessCodeDeleteConfirmOpen, setAccessCodeDeleteConfirmOpen] = useState(false);
  const [accessCodeToDelete, setAccessCodeToDelete] = useState<string | null>(null);
  const [accessCodeForm, setAccessCodeForm] = useState<AccessCodeCreateData>({
    code: '',
    name: '',
    max_users: 30,
    max_video_generations: 5,
  });

  // 액세스 코드 사용자 목록 관련 state
  const [accessCodeUsersOpen, setAccessCodeUsersOpen] = useState(false);
  const [selectedAccessCode, setSelectedAccessCode] = useState<AccessCode | null>(null);
  const [accessCodeUsers, setAccessCodeUsers] = useState<AccessCodeUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // API 키 통계 관련 state
  const [apiKeyStats, setApiKeyStats] = useState<{
    text: { total: number; available: number; };
    image: { total: number; available: number; };
    video: { total: number; available: number; };
  } | null>(null);

  useEffect(() => {
    if (isAdminLoggedIn()) {
      loadAccessCodes();
      loadApiKeyStats();
    }
  }, []);



  const loadAccessCodes = async () => {
    try {
      setLoading(true);
      setError(null);
      const codes = await getAllAccessCodes();
      setAccessCodes(codes);
    } catch (err: any) {
      console.error('액세스 코드 로드 오류:', err);
      setError(err.response?.data?.detail || '액세스 코드를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const loadApiKeyStats = async () => {
    try {
      // 기존 dashboard API에서 API 키 정보만 추출
      const response = await api.get('/sessions/dashboard/data');
      const dashboardData = response.data;
      
      if (dashboardData.api_keys_by_type) {
        setApiKeyStats(dashboardData.api_keys_by_type);
      }
    } catch (err: any) {
      console.error('API 키 통계 로드 오류:', err);
      // API 키 통계는 필수가 아니므로 에러를 표시하지 않음
    }
  };

  const handleCreateAccessCode = async () => {
    try {
      await createAccessCode(accessCodeForm);
      setCreateAccessCodeOpen(false);
      setAccessCodeForm({
        code: '',
        name: '',
        max_users: 30,
        max_video_generations: 5,
      });
      await loadAccessCodes();
    } catch (err: any) {
      console.error('액세스 코드 생성 오류:', err);
      alert(err.response?.data?.detail || '액세스 코드 생성에 실패했습니다.');
    }
  };

  const handleEditAccessCode = (accessCode: AccessCode) => {
    setAccessCodeToEdit(accessCode);
    setAccessCodeForm({
      code: accessCode.code,
      name: accessCode.name || '',
      max_users: accessCode.max_users,
      max_video_generations: accessCode.max_video_generations,
    });
    setEditAccessCodeOpen(true);
  };

  const handleUpdateAccessCode = async () => {
    if (!accessCodeToEdit) return;
    
    try {
      await updateAccessCode(accessCodeToEdit.id, accessCodeForm);
      setEditAccessCodeOpen(false);
      setAccessCodeToEdit(null);
      setAccessCodeForm({
        code: '',
        name: '',
        max_users: 30,
        max_video_generations: 5,
      });
      await loadAccessCodes();
    } catch (err: any) {
      console.error('액세스 코드 수정 오류:', err);
      alert(err.response?.data?.detail || '액세스 코드 수정에 실패했습니다.');
    }
  };

  const handleDeleteAccessCode = (codeId: string) => {
    setAccessCodeToDelete(codeId);
    setAccessCodeDeleteConfirmOpen(true);
  };

  const confirmDeleteAccessCode = async () => {
    if (!accessCodeToDelete) return;
    
    try {
      await deleteAccessCode(accessCodeToDelete);
      await loadAccessCodes();
      setAccessCodeDeleteConfirmOpen(false);
      setAccessCodeToDelete(null);
    } catch (err: any) {
      console.error('액세스 코드 삭제 오류:', err);
      alert(err.response?.data?.detail || '액세스 코드 삭제에 실패했습니다.');
      setAccessCodeDeleteConfirmOpen(false);
      setAccessCodeToDelete(null);
    }
  };

  const handleViewAccessCodeUsers = async (accessCode: AccessCode) => {
    setSelectedAccessCode(accessCode);
    setAccessCodeUsersOpen(true);
    setLoadingUsers(true);
    
    try {
      console.log('활성 사용자 조회 시작:', accessCode.code);
      const users = await getActiveUsers(accessCode.code);
      console.log('활성 사용자 조회 성공:', users);
      setAccessCodeUsers(users);
    } catch (err: any) {
      console.error('사용자 목록 조회 상세 오류:', {
        message: err.message,
        response: err.response,
        status: err.response?.status,
        data: err.response?.data,
        accessCode: accessCode.code
      });
      
      const errorMessage = err.response?.data?.detail || err.message || '사용자 목록 조회에 실패했습니다.';
      console.error('최종 에러 메시지:', errorMessage);
      alert(`활성 사용자 조회 실패: ${errorMessage}`);
      setAccessCodeUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };



  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('ko-KR');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'expired': return 'warning';
      case 'terminated': return 'error';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress size={60} sx={{ color: 'white' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Container maxWidth="sm">
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Button 
            variant="contained" 
            onClick={() => {
              loadAccessCodes();
              loadApiKeyStats();
            }} 
            fullWidth
          >
            다시 시도
          </Button>
        </Container>
      </Box>
    );
  }



  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      }}
    >
      <Navbar />
      
      <Container maxWidth="xl" sx={{ pt: 4, pb: 4 }}>
        {/* 페이지 헤더 */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Typography
            variant="h4"
            sx={{
              color: 'white',
              fontWeight: 'bold',
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
            }}
          >
            관리자 대시보드
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={() => {
                loadAccessCodes();
                loadApiKeyStats();
              }}
              sx={{
                background: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                color: 'white',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.3)',
                },
              }}
            >
              새로고침
            </Button>
            <Button
              variant="contained"
              startIcon={<KeyIcon />}
              onClick={() => setCreateAccessCodeOpen(true)}
              sx={{
                background: 'rgba(76, 175, 80, 0.2)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(76, 175, 80, 0.3)',
                color: 'white',
                '&:hover': {
                  background: 'rgba(76, 175, 80, 0.3)',
                },
              }}
            >
              액세스 코드 생성
            </Button>
          </Box>
        </Box>

        {/* API 키 통계 카드들 */}
        {apiKeyStats && (
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {/* 텍스트 API 키 */}
            <Grid item xs={12} sm={6} md={4}>
              <Card
                sx={{
                  background: 'rgba(76, 175, 80, 0.1)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(76, 175, 80, 0.3)',
                  borderRadius: 3,
                  height: '100%',
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <KeyIcon sx={{ color: '#4caf50', mr: 1 }} />
                    <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.9rem' }}>
                      텍스트 API
                    </Typography>
                  </Box>
                  <Typography variant="h4" sx={{ color: 'white', mb: 1 }}>
                    {apiKeyStats.text?.total || 0}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.75rem' }}>
                    사용가능: {apiKeyStats.text?.available || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* 이미지 API 키 */}
            <Grid item xs={12} sm={6} md={4}>
              <Card
                sx={{
                  background: 'rgba(33, 150, 243, 0.1)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(33, 150, 243, 0.3)',
                  borderRadius: 3,
                  height: '100%',
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <KeyIcon sx={{ color: '#2196f3', mr: 1 }} />
                    <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.9rem' }}>
                      이미지 API
                    </Typography>
                  </Box>
                  <Typography variant="h4" sx={{ color: 'white', mb: 1 }}>
                    {apiKeyStats.image?.total || 0}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.75rem' }}>
                    사용가능: {apiKeyStats.image?.available || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* 비디오 API 키 */}
            <Grid item xs={12} sm={6} md={4}>
              <Card
                sx={{
                  background: 'rgba(156, 39, 176, 0.1)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(156, 39, 176, 0.3)',
                  borderRadius: 3,
                  height: '100%',
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <KeyIcon sx={{ color: '#9c27b0', mr: 1 }} />
                    <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.9rem' }}>
                      비디오 API
                    </Typography>
                  </Box>
                  <Typography variant="h4" sx={{ color: 'white', mb: 1 }}>
                    {apiKeyStats.video?.total || 0}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.75rem' }}>
                    사용가능: {apiKeyStats.video?.available || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* 액세스 코드 목록 */}
        <Paper
          sx={{
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: 3,
            mb: 4,
          }}
        >
          <Box sx={{ p: 3, borderBottom: '1px solid rgba(255, 255, 255, 0.2)' }}>
            <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
              액세스 코드 관리
            </Typography>
          </Box>
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>코드</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>이름</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>상태</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>사용자</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>생성일</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>작업</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {accessCodes.map((accessCode) => (
                  <TableRow key={accessCode.id}>
                    <TableCell>
                      <Box
                        sx={{ 
                          color: 'white',
                          fontFamily: 'monospace',
                          fontWeight: 'bold',
                          padding: '8px 12px',
                          borderRadius: '8px',
                          display: 'inline-block',
                          background: accessCode.is_active 
                            ? 'rgba(76, 175, 80, 0.1)' 
                            : 'rgba(158, 158, 158, 0.1)',
                          border: accessCode.is_active 
                            ? '1px solid rgba(76, 175, 80, 0.3)' 
                            : '1px solid rgba(158, 158, 158, 0.3)',
                        }}
                      >
                        {accessCode.code}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: 'white' }}>
                      {accessCode.name || '-'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={accessCode.is_active ? '활성' : '비활성'}
                        color={accessCode.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={{ color: 'white' }}>
                      {accessCode.current_users}/{accessCode.max_users}
                    </TableCell>
                    <TableCell sx={{ color: 'white' }}>
                      {formatDateTime(accessCode.created_at)}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="활성 사용자 보기">
                          <IconButton
                            size="small"
                            onClick={() => handleViewAccessCodeUsers(accessCode)}
                            sx={{ color: '#4caf50' }}
                            disabled={accessCode.current_users === 0}
                          >
                            <PeopleIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="수정">
                          <IconButton
                            size="small"
                            onClick={() => handleEditAccessCode(accessCode)}
                            sx={{ color: '#2196f3' }}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="삭제">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteAccessCode(accessCode.id)}
                            sx={{ color: '#ff6b6b' }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                {accessCodes.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ textAlign: 'center', color: 'rgba(255, 255, 255, 0.6)', py: 4 }}>
                      생성된 액세스 코드가 없습니다.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

      </Container>

      {/* 액세스 코드 생성 다이얼로그 */}
      <Dialog
        open={createAccessCodeOpen}
        onClose={() => setCreateAccessCodeOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: 'rgba(25, 25, 25, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle sx={{ color: 'white', fontWeight: 'bold' }}>
          새 액세스 코드 생성
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="액세스 코드"
            fullWidth
            variant="outlined"
            value={accessCodeForm.code}
            onChange={(e) => setAccessCodeForm(prev => ({ ...prev, code: e.target.value }))}
            placeholder="영문, 숫자 조합 (예: CLASS2024)"
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                color: 'white',
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#4caf50',
                },
              },
              '& .MuiInputLabel-root': {
                color: 'rgba(255, 255, 255, 0.7)',
                '&.Mui-focused': {
                  color: '#4caf50',
                },
              },
            }}
          />
          <TextField
            margin="dense"
            label="코드 이름/설명 (선택사항)"
            fullWidth
            variant="outlined"
            value={accessCodeForm.name}
            onChange={(e) => setAccessCodeForm(prev => ({ ...prev, name: e.target.value }))}
            placeholder="예: 2024년 1학기 AI 수업"
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                color: 'white',
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#4caf50',
                },
              },
              '& .MuiInputLabel-root': {
                color: 'rgba(255, 255, 255, 0.7)',
                '&.Mui-focused': {
                  color: '#4caf50',
                },
              },
            }}
          />
          <TextField
            margin="dense"
            label="최대 사용자 수"
            type="number"
            fullWidth
            variant="outlined"
            value={accessCodeForm.max_users}
            onChange={(e) => {
              const value = e.target.value;
              if (value === '') {
                setAccessCodeForm(prev => ({ ...prev, max_users: 0 }));
              } else {
                const num = parseInt(value);
                setAccessCodeForm(prev => ({ ...prev, max_users: isNaN(num) ? 30 : Math.max(1, Math.min(300, num)) }));
              }
            }}
            inputProps={{ min: 1, max: 300 }}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                color: 'white',
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#4caf50',
                },
              },
              '& .MuiInputLabel-root': {
                color: 'rgba(255, 255, 255, 0.7)',
                '&.Mui-focused': {
                  color: '#4caf50',
                },
              },
            }}
          />
          <TextField
            margin="dense"
            label="사용자당 최대 영상 생성 횟수"
            type="number"
            fullWidth
            variant="outlined"
            value={accessCodeForm.max_video_generations}
            onChange={(e) => {
              const value = e.target.value;
              if (value === '') {
                setAccessCodeForm(prev => ({ ...prev, max_video_generations: 0 }));
              } else {
                const num = parseInt(value);
                setAccessCodeForm(prev => ({ ...prev, max_video_generations: isNaN(num) ? 5 : Math.max(0, Math.min(20, num)) }));
              }
            }}
            inputProps={{ min: 0, max: 20 }}
            helperText="0으로 설정하면 영상 생성 기능이 비활성화됩니다"
            sx={{
              '& .MuiOutlinedInput-root': {
                color: 'white',
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#4caf50',
                },
              },
              '& .MuiInputLabel-root': {
                color: 'rgba(255, 255, 255, 0.7)',
                '&.Mui-focused': {
                  color: '#4caf50',
                },
              },
              '& .MuiFormHelperText-root': {
                color: 'rgba(255, 255, 255, 0.6)',
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setCreateAccessCodeOpen(false)}
            sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
          >
            취소
          </Button>
          <Button 
            onClick={handleCreateAccessCode}
            variant="contained"
            sx={{
              background: '#4caf50',
              '&:hover': {
                background: '#45a049',
              },
            }}
          >
            생성
          </Button>
        </DialogActions>
      </Dialog>

      {/* 액세스 코드 수정 다이얼로그 */}
      <Dialog
        open={editAccessCodeOpen}
        onClose={() => setEditAccessCodeOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: 'rgba(25, 25, 25, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle sx={{ color: 'white', fontWeight: 'bold' }}>
          액세스 코드 수정
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="액세스 코드"
            fullWidth
            variant="outlined"
            value={accessCodeForm.code}
            onChange={(e) => setAccessCodeForm(prev => ({ ...prev, code: e.target.value }))}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                color: 'white',
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#2196f3',
                },
              },
              '& .MuiInputLabel-root': {
                color: 'rgba(255, 255, 255, 0.7)',
                '&.Mui-focused': {
                  color: '#2196f3',
                },
              },
            }}
          />
          <TextField
            margin="dense"
            label="코드 이름/설명"
            fullWidth
            variant="outlined"
            value={accessCodeForm.name}
            onChange={(e) => setAccessCodeForm(prev => ({ ...prev, name: e.target.value }))}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                color: 'white',
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#2196f3',
                },
              },
              '& .MuiInputLabel-root': {
                color: 'rgba(255, 255, 255, 0.7)',
                '&.Mui-focused': {
                  color: '#2196f3',
                },
              },
            }}
          />
          <TextField
            margin="dense"
            label="최대 사용자 수"
            type="number"
            fullWidth
            variant="outlined"
            value={accessCodeForm.max_users}
            onChange={(e) => {
              const value = e.target.value;
              if (value === '') {
                setAccessCodeForm(prev => ({ ...prev, max_users: 0 }));
              } else {
                const num = parseInt(value);
                setAccessCodeForm(prev => ({ ...prev, max_users: isNaN(num) ? 30 : Math.max(1, Math.min(300, num)) }));
              }
            }}
            inputProps={{ min: 1, max: 300 }}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                color: 'white',
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#2196f3',
                },
              },
              '& .MuiInputLabel-root': {
                color: 'rgba(255, 255, 255, 0.7)',
                '&.Mui-focused': {
                  color: '#2196f3',
                },
              },
            }}
          />
          <TextField
            margin="dense"
            label="사용자당 최대 영상 생성 횟수"
            type="number"
            fullWidth
            variant="outlined"
            value={accessCodeForm.max_video_generations}
            onChange={(e) => {
              const value = e.target.value;
              if (value === '') {
                setAccessCodeForm(prev => ({ ...prev, max_video_generations: 0 }));
              } else {
                const num = parseInt(value);
                setAccessCodeForm(prev => ({ ...prev, max_video_generations: isNaN(num) ? 5 : Math.max(0, Math.min(20, num)) }));
              }
            }}
            inputProps={{ min: 0, max: 20 }}
            helperText="0으로 설정하면 영상 생성 기능이 비활성화됩니다"
            sx={{
              '& .MuiOutlinedInput-root': {
                color: 'white',
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#2196f3',
                },
              },
              '& .MuiInputLabel-root': {
                color: 'rgba(255, 255, 255, 0.7)',
                '&.Mui-focused': {
                  color: '#2196f3',
                },
              },
              '& .MuiFormHelperText-root': {
                color: 'rgba(255, 255, 255, 0.6)',
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setEditAccessCodeOpen(false)}
            sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
          >
            취소
          </Button>
          <Button 
            onClick={handleUpdateAccessCode}
            variant="contained"
            sx={{
              background: '#2196f3',
              '&:hover': {
                background: '#1976d2',
              },
            }}
          >
            수정
          </Button>
        </DialogActions>
      </Dialog>

      {/* 액세스 코드 삭제 확인 다이얼로그 */}
      <Dialog
        open={accessCodeDeleteConfirmOpen}
        onClose={() => setAccessCodeDeleteConfirmOpen(false)}
        PaperProps={{
          sx: {
            background: 'rgba(25, 25, 25, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle sx={{ color: 'white', fontWeight: 'bold' }}>
          액세스 코드 삭제 확인
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: 'white' }}>
            정말로 이 액세스 코드를 삭제하시겠습니까?
          </Typography>
          <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', mt: 1 }}>
            삭제된 액세스 코드는 복구할 수 없으며, 해당 코드를 사용하는 모든 사용자의 접근이 차단됩니다.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setAccessCodeDeleteConfirmOpen(false)}
            sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
          >
            취소
          </Button>
          <Button 
            onClick={confirmDeleteAccessCode}
            variant="contained"
            sx={{
              background: '#f44336',
              '&:hover': {
                background: '#d32f2f',
              },
            }}
          >
            삭제
          </Button>
        </DialogActions>
      </Dialog>



      {/* 액세스 코드 삭제 확인 다이얼로그 */}
      <Dialog
        open={accessCodeDeleteConfirmOpen}
        onClose={() => setAccessCodeDeleteConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: 3,
          }
        }}
      >
        <DialogTitle sx={{ color: 'white', fontWeight: 'bold' }}>
          액세스 코드 삭제 확인
        </DialogTitle>
        
        <DialogContent>
          <Typography sx={{ color: 'white', mb: 2 }}>
            정말로 이 액세스 코드를 삭제하시겠습니까?
          </Typography>
          <Typography sx={{ color: 'rgba(255, 255, 255, 0.8)', fontFamily: 'monospace' }}>
            모든 사용자 연결이 해제됩니다.
          </Typography>
        </DialogContent>
        
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={() => {
              setAccessCodeDeleteConfirmOpen(false);
              setAccessCodeToDelete(null);
            }}
            sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
          >
            취소
          </Button>
          <Button
            onClick={confirmDeleteAccessCode}
            variant="contained"
            color="error"
            sx={{
              background: '#ff6b6b',
              '&:hover': {
                background: '#ff5252',
              },
            }}
          >
            삭제
          </Button>
        </DialogActions>
      </Dialog>

      {/* 액세스 코드 사용자 목록 다이얼로그 */}
      <Dialog
        open={accessCodeUsersOpen}
        onClose={() => {
          setAccessCodeUsersOpen(false);
          setSelectedAccessCode(null);
          setAccessCodeUsers([]);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: 3,
          }
        }}
      >
        <DialogTitle sx={{ color: 'white', fontWeight: 'bold' }}>
          활성 사용자 목록 - {selectedAccessCode?.code} ({selectedAccessCode?.name || '이름 없음'})
        </DialogTitle>
        
        <DialogContent>
          {loadingUsers ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress sx={{ color: 'white' }} />
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>사용자 번호</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>API 키 인덱스</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>상태</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>영상 생성</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>접속 시간</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>최근 활동</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>IP 주소</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {accessCodeUsers.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell sx={{ color: 'white' }}>
                        #{user.user_number}
                      </TableCell>
                      <TableCell sx={{ color: 'white' }}>
                        {user.api_key_index}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.is_active ? '활성' : '비활성'}
                          color={user.is_active ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell sx={{ color: 'white' }}>
                        {user.video_generation_count || 0}회
                      </TableCell>
                      <TableCell sx={{ color: 'white' }}>
                        {formatDateTime(user.connected_at)}
                      </TableCell>
                      <TableCell sx={{ color: 'white' }}>
                        {formatDateTime(user.last_activity)}
                      </TableCell>
                      <TableCell sx={{ color: 'white' }}>
                        {user.ip_address || '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                  {accessCodeUsers.length === 0 && !loadingUsers && (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ textAlign: 'center', color: 'rgba(255, 255, 255, 0.6)', py: 4 }}>
                        현재 활성 사용자가 없습니다.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={() => {
              setAccessCodeUsersOpen(false);
              setSelectedAccessCode(null);
              setAccessCodeUsers([]);
            }}
            sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
          >
            닫기
          </Button>
          <Button
            onClick={() => handleViewAccessCodeUsers(selectedAccessCode!)}
            disabled={!selectedAccessCode}
            variant="contained"
            startIcon={<RefreshIcon />}
            sx={{
              background: '#4caf50',
              '&:hover': {
                background: '#45a049',
              },
            }}
          >
            새로고침
          </Button>
        </DialogActions>
      </Dialog>


    </Box>
  );
};

export default AdminDashboardPage; 